import { useState, useRef, useContext } from 'react';
import { useAlert } from '../functions/useAlert';
import { DataContext } from '../contexts/DataProvider';
import { uploadAgreement } from '../services/agreements.service';
import type { Agreement } from './useAgreements';

const getFileExtension = (name: string) => name.split('.').pop()?.toLowerCase() || '';

interface AgreementUploadState {
  isUploading: boolean;
  uploadProgress: number;
  isDragOver: boolean;
  stagedFile: File | null;
  setStagedFile: (f: File | null) => void;
  templateName: string;
  setTemplateName: (n: string) => void;
  uploadModalOpen: boolean;
  setUploadModalOpen: (open: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadFile: () => Promise<void>;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent) => void;
  handleBrowse: (e: React.ChangeEvent<HTMLInputElement>) => void;
  resetUploadState: () => void;
}

export const useAgreementUpload = (
  selectedOrganization?: string,
  onUploadSuccess?: (agreement: Agreement) => void,
  onRefetch?: () => void,
): AgreementUploadState => {
  const [setAlert] = useAlert();
  const { login }: any = useContext(DataContext);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isGlobalAdmin = login?.data?.role === 'global admin';

  const resetUploadState = () => {
    setStagedFile(null);
    setTemplateName('');
    setUploadModalOpen(false);
  };

  const uploadFile = async () => {
    if (!stagedFile) return;
    if (getFileExtension(stagedFile.name) !== 'pdf') {
      setAlert('Only PDF files are supported. Please convert your document to PDF format before uploading.', 'error');
      return;
    }
    if (stagedFile.size > 25 * 1024 * 1024) {
      setAlert('File size must be under 25 MB.', 'error');
      return;
    }
    if (isGlobalAdmin && (!selectedOrganization || selectedOrganization === 'All Organisation')) {
      setAlert('Please select a specific organisation before uploading agreements.', 'error');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => Math.min(prev + 15, 85));
    }, 200);

    try {
      const orgId = isGlobalAdmin
        ? selectedOrganization!
        : (selectedOrganization && selectedOrganization !== 'All Organisation'
          ? selectedOrganization
          : login.data.orgId);

      const payload = new FormData();
      payload.append('file', stagedFile);
      payload.append('orgId', orgId);
      payload.append('userId', login.data.id);
      payload.append('uploadedBy', login.data.role);
      if (templateName.trim()) payload.append('templateName', templateName.trim());

      const resp: any = await uploadAgreement(payload);
      clearInterval(progressInterval);
      setUploadProgress(100);
      if (resp?.hasError) throw new Error(resp.message);

      setAlert(resp?.message || 'Agreement uploaded successfully!', 'success');
      resetUploadState();
      onRefetch?.();
      if (resp?.data) onUploadSuccess?.(resp.data);
    } catch (err: any) {
      setAlert(err.message || 'Upload failed.', 'error');
    } finally {
      clearInterval(progressInterval);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const stageFile = (file: File) => {
    setStagedFile(file);
    if (!templateName.trim()) setTemplateName(file.name.replace(/\.[^/.]+$/, ''));
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) stageFile(file);
  };
  const handleBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) stageFile(file);
    if (e.target) e.target.value = '';
  };

  return {
    isUploading, uploadProgress, isDragOver,
    stagedFile, setStagedFile,
    templateName, setTemplateName,
    uploadModalOpen, setUploadModalOpen,
    fileInputRef, uploadFile,
    handleDragOver, handleDragLeave, handleDrop, handleBrowse,
    resetUploadState,
  };
};
